/// <reference path="../../css.d.ts" />
"use client";
// @ubm/ui/components/checkbox — LA case à cocher canonique (lot U1, 05/10/2026).
// Source : UIArc `registry/components/checkbox/checkbox.tsx` @ 7238f98196b147a8d50ce467abcbad310d4d8414 (MIT) — carré qui se
// remplit sur ressort, coche dessinée (pathLength) qui se MORPHE en tiret pour l'état mixte, appui 0,95, mouvement réduit.
// Moteur unique (arbitrage Root : canon UIArc par défaut) : l'ancien `primitives/Checkbox` (Radix wrappé) est DÉPLACÉ ici ;
// `@ubm/ui` Checkbox et `@ubm/product-ui` Checkbox en sont des façades. Sémantique Radix conservée : role checkbox,
// aria-checked (true/false/mixed), `checked` / `defaultChecked` / `indeterminate`, `disabled`, `name` / `value` / `required` /
// `form` (input de formulaire Radix), ref sur le bouton, `onCheckedChange`.
// Adaptations : `label` / `description` FACULTATIFS — sans eux, aucune enveloppe ; aucun `aria-label` inventé (la source posait
// « Checkbox » par défaut) ; classes historiques (`ubm-ui-checkbox`, utilitaires, `invalid`) conservées sur le bouton ;
// `children` facultatifs rendus après la marque (glyphe d'une façade). Peinture effective : module (hors couche).

import { forwardRef, useId, useState } from "react";
import type { ComponentPropsWithoutRef, ElementRef, ReactNode, CSSProperties } from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { motion, useReducedMotionConfig as useReducedMotion } from "motion/react";
import type { Transition } from "motion/react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/cn";
import { motionTokens } from "../../lib/motion-tokens";
// Fondation UIArc (jetons --arc-*) embarquée par le composant : aucun import d'app ni fournisseur requis.
import "../../styles/foundation.css";
import styles from "./checkbox.module.css";
import tokens from "../../../tokens.json";

const checkboxVariants = cva(
  "ubm-ui-checkbox inline-flex shrink-0 items-center justify-center bg-surface border border-line text-on-action " +
    "transition-colors rounded-sm " +
    "data-[state=checked]:bg-action data-[state=checked]:border-action " +
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring " +
    "disabled:opacity-60 disabled:pointer-events-none aria-[invalid=true]:border-danger-line",
  {
    variants: {
      size: {
        sm: "size-control-sm",
        md: "size-control-md",
        lg: "size-control-lg",
      },
    },
    defaultVariants: { size: "md" },
  },
);

export interface CheckboxProps
  extends ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    VariantProps<typeof checkboxVariants> {
  /** Libellé visible (API UIArc) relié au bouton. Absent : aucune enveloppe (libellé fourni par le consommateur). */
  label?: ReactNode;
  /** Aide visible (API UIArc), reliée par `aria-describedby`. */
  description?: ReactNode;
  /** État d'erreur : pose `aria-invalid` (l'enveloppe Field le branche aussi). */
  invalid?: boolean;
}

/** Both marks share three points, so the check morphs into the dash and back instead of swapping. */
const checkPath = "M4.25 9.25 L7.25 12.25 L13.75 5.75";
const dashPath = "M4.75 9 L9 9 L13.25 9";

export const Checkbox = forwardRef<ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(function Checkbox(
  { label, description, id, className, size, invalid, "aria-invalid": ariaInvalid, style, checked, defaultChecked, onCheckedChange, children, ...props }, ref,
) {
  const generatedId = useId();
  const wrapped = label != null || description != null;
  const controlId = id ?? (wrapped ? generatedId : undefined);
  const descriptionId = description != null ? `${controlId}-description` : undefined;
  const reduced = useReducedMotion();
  const [internal, setInternal] = useState<CheckboxPrimitive.CheckedState>(defaultChecked ?? false);
  const state = checked ?? internal;
  const on = state !== false;
  const change = (next: CheckboxPrimitive.CheckedState) => { if (checked === undefined) setInternal(next); onCheckedChange?.(next); };
  const fade: Transition = { duration: on ? motionTokens.duration.instant : motionTokens.duration.fast, ease: [...motionTokens.ease.standard] };
  const describedBy = [props["aria-describedby"], descriptionId].filter(Boolean).join(" ") || undefined;
  const box = (
    <CheckboxPrimitive.Root
      {...props}
      id={controlId}
      ref={ref}
      checked={state}
      onCheckedChange={change}
      aria-invalid={ariaInvalid ?? (invalid ? true : undefined)}
      data-ubm-canon=""
      data-size={size ?? "md"}
      style={{ "--checkbox-mark-size": `${tokens.component.checkbox.sizes[size ?? "md"]}px`, "--checkbox-mark-radius": `${tokens.component.checkbox.markRadius}px`, ...style } as CSSProperties}
      className={cn(checkboxVariants({ size }), styles.box, className)}
      aria-describedby={describedBy}
    >
      <span className={styles.visual} aria-hidden="true">
        <motion.span className={styles.fill} initial={false} animate={{ opacity: on ? 1 : 0, scale: on ? 1 : .6 }} transition={reduced ? { duration: 0 } : { scale: motionTokens.spring.snappy, opacity: fade }} />
        <svg className={styles.mark} viewBox="0 0 18 18" fill="none" focusable="false">
          <motion.path initial={false} animate={{ d: state === "indeterminate" ? dashPath : checkPath, pathLength: on ? 1 : 0, opacity: on ? 1 : 0 }} transition={reduced ? { duration: 0 } : { d: motionTokens.spring.morph, pathLength: motionTokens.spring.snappy, opacity: fade }} stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      {children}
    </CheckboxPrimitive.Root>
  );
  if (!wrapped) return box;
  return <div className={styles.field}>
    {box}
    <div className={styles.copy}>{label != null ? <label htmlFor={controlId}>{label}</label> : null}{description != null ? <span id={descriptionId}>{description}</span> : null}</div>
  </div>;
});

Checkbox.displayName = "Checkbox";

export { checkboxVariants };

export default Checkbox;
